import { BaseSqliteRepository } from './base-sqlite.repository';
import { ValidationError } from '@app/domain/entities';
import type { ICurrencyRepository } from '@app/domain/ports';
import type { Currency, AvailableCurrency } from '@app/domain/entities';
import { SqliteQueryError } from '../../errors';

type CurrencyRow = Omit<Currency, 'isDefault' | 'hidden'> & { isDefault: number; hidden: number };

export class CurrencyRepository extends BaseSqliteRepository implements ICurrencyRepository {
  async findAll(): Promise<Currency[]> {
    const currencies = await this.query<CurrencyRow>(
      `
        SELECT
          cl.ticker AS code,
          c.full_name AS name,
          c.short_name AS nameShort,
          cl.locale AS locale,
          cl.iso_code AS isoCode,
          c.is_default AS isDefault,
          c.position AS position,
          CAST(c.is_hidden AS INTEGER) AS hidden
        FROM currencies AS c
        INNER JOIN available_currencies AS cl
          ON cl._id == c.available_currency_id
        WHERE c.is_deleted = 0
        ORDER BY position ASC
      `,
    );

    return currencies.map(this.mapRowToEntity);
  }

  async getByCode(code: string): Promise<Currency | null> {
    const currency = await this.queryOne<CurrencyRow>(
      `
        SELECT
          cl.ticker AS code,
          c.full_name AS name,
          c.short_name AS nameShort,
          cl.locale AS locale,
          cl.iso_code AS isoCode,
          c.is_default AS isDefault,
          c.position AS position,
          CAST(c.is_hidden AS INTEGER) AS hidden
        FROM currencies AS c
        INNER JOIN available_currencies AS cl
          ON cl._id == c.available_currency_id
        WHERE c.is_deleted = 0
        AND cl.ticker = ?
      `,
      [code],
    );

    return currency ? this.mapRowToEntity(currency) : null;
  }

  async findAvailableCurrencies(): Promise<AvailableCurrency[]> {
    const available = await this.query<AvailableCurrency>(
      `
        SELECT
          cl.ticker AS code,
          cl.name AS name,
          cl.short_name AS nameShort
        FROM available_currencies AS cl
        LEFT JOIN currencies c
          ON c.available_currency_id == cl._id
          AND c.is_deleted = 0
        WHERE cl.is_deleted = 0
          AND c._id IS NULL
        ORDER BY cl.name ASC
      `,
    );

    return available;
  }

  async insert(currency: Omit<Currency, 'locale' | 'position' | 'isoCode'>): Promise<number> {
    // Get the available_currency ID for this currency code
    const currencyListRow = await this.queryOne<{ id: number; name: string; nameShort: string }>(
      `SELECT
        _id AS id,
        name AS name,
        short_name AS nameShort
       FROM available_currencies
       WHERE ticker = ? AND is_deleted = 0`,
      [currency.code],
    );

    if (!currencyListRow)
      throw new ValidationError('currencyCode', `Currency code '${currency.code}' not found in available_currencies`);

    // Calculate next position as MAX + 1
    const maxPositionRow = await this.queryOne<{ maxPosition: number | null }>(
      `SELECT MAX(position) AS maxPosition FROM currencies WHERE is_deleted = 0`,
    );
    const nextPosition = (maxPositionRow?.maxPosition ?? 0) + 1;

    // Insert currency
    const result = await this.queryOne<{ id: number }>(
      `INSERT INTO currencies (position, available_currency_id, name, full_name, short_name, is_default, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, strftime('%s','now'))
       RETURNING _id AS id`,
      [
        nextPosition,
        currencyListRow.id,
        currencyListRow.name,
        currency.name,
        currency.nameShort,
        currency.isDefault ? 1 : 0,
      ],
    );

    if (!result?.id) throw new SqliteQueryError('INSERT INTO currencies', [], 'Failed to insert currency');

    return result.id;
  }

  async update(currency: Omit<Currency, 'locale' | 'isoCode'>): Promise<void> {
    await this.execute(
      `UPDATE currencies
       SET full_name = ?,
           short_name = ?,
           is_default = ?,
           position = ?,
           is_hidden = ?,
           updated_at = strftime('%s','now')
       WHERE is_deleted = 0
       AND _id IN (
         SELECT c._id
         FROM currencies c
         INNER JOIN available_currencies cl
          ON cl._id == c.available_currency_id
         WHERE
          cl.ticker = ?
          AND c.is_deleted = 0
       )`,
      [
        currency.name,
        currency.nameShort,
        currency.isDefault ? 1 : 0,
        currency.position,
        currency.hidden ? 1 : 0,
        currency.code,
      ],
    );
  }

  async delete(code: string): Promise<void> {
    await this.execute(
      `UPDATE currencies
       SET is_deleted = 1,
           updated_at = strftime('%s','now')
       WHERE is_deleted = 0
       AND _id IN (
         SELECT c._id
         FROM currencies c
         INNER JOIN available_currencies cl
          ON cl._id == c.available_currency_id
         WHERE
          cl.ticker = ?
          AND c.is_deleted = 0
       )`,
      [code],
    );
  }

  private mapRowToEntity(row: CurrencyRow): Currency {
    return { ...row, isDefault: Boolean(row.isDefault), hidden: Boolean(row.hidden) };
  }
}
